import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';

export default function ProfessionalInviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [relationshipId, setRelationshipId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [decision, setDecision] = useState('');

  const authToken = useMemo(() => localStorage.getItem('authToken') || '', []);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError('');
        setMessage('');
        const res = await axios.get(`${API_BASE_URL}/api/professional/invite/${token}`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        setInvite(res.data?.invite || null);
        setRelationshipId(res.data?.relationshipId || null);
      } catch (err) {
        if (err?.response?.status === 401) {
          navigate(`/giris-yap?next=${encodeURIComponent(window.location.pathname)}`);
          return;
        }
        setError(err?.response?.data?.error || 'Davet bağlantısı açılamadı.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [authToken, token, navigate]);

  const postDecision = async (action) => {
    if (!relationshipId) return;
    try {
      setSubmitting(true);
      setError('');
      setMessage('');

      if (action === 'accept') {
        await axios.post(
          `${API_BASE_URL}/api/professional/consent/${relationshipId}/accept`,
          { consentVersion: 'v1', consentSource: 'user_app' },
          { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} }
        );
        setDecision('accepted');
        setMessage('Erişim onaylandı. Uzmanınız artık mutfak akışınızı takip edebilir.');
      } else {
        await axios.post(
          `${API_BASE_URL}/api/professional/consent/${relationshipId}/reject`,
          {},
          { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} }
        );
        setDecision('rejected');
        setMessage('Erişim isteği reddedildi. Bu uzman verilerinizi göremez.');
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(`/giris-yap?next=${encodeURIComponent(window.location.pathname)}`);
        return;
      }
      setError(err?.response?.data?.error || 'İşlem sırasında hata oluştu.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="text-sm font-semibold text-slate-600 dark:text-slate-300">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 px-4 py-12">
      <div className="mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h1 className="text-2xl font-black text-slate-900 dark:text-white">Uzman Erişim İsteği</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Onay verirseniz uzmanınız günlük takip verilerinizi görebilir. Reddederseniz erişim açılmaz.
        </p>

        {invite ? (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Uzman</div>
            <div className="mt-1 text-base font-semibold text-slate-900 dark:text-white">
              {invite?.professional?.name || invite?.professional?.email || 'Uzman'}
            </div>
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Uzman tipi: {invite?.professionalType === 'pt' ? 'PT' : 'Diyetisyen'}
            </div>
            <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Son geçerlilik: {invite?.expiresAt ? new Date(invite.expiresAt).toLocaleString('tr-TR') : '-'}
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-300">
            {error}
          </div>
        ) : null}

        {message ? (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-300">
            {message}
          </div>
        ) : null}

        <div className="mt-6 flex gap-3">
          {decision !== 'accepted' && (
            <button
              onClick={() => postDecision('accept')}
              disabled={submitting || !relationshipId}
              className="inline-flex items-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60 hover:bg-emerald-700"
            >
              {submitting ? 'İşleniyor...' : 'Onayla'}
            </button>
          )}
          {decision !== 'rejected' && (
            <button
              onClick={() => postDecision('reject')}
              disabled={submitting || !relationshipId}
              className="inline-flex items-center rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60 hover:bg-rose-700"
            >
              {submitting ? 'İşleniyor...' : 'Reddet'}
            </button>
          )}
          <button
            onClick={() => navigate('/notifications')}
            className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            Bildirimlere Dön
          </button>
        </div>
      </div>
    </div>
  );
}
